/**
 * `/vehicles/:id` — customer-facing detail of a single vehicle.
 *
 * Phase 2 Stream B of the PSM → Customer Portal migration. Adapted from
 * the legacy PSM `portal/src/pages/VehicleDetailPage.tsx`; the
 * lightbox / tabs / responsive split are replaced by the simpler
 * inline-style primitives shipped by the Phase 1 foundation so we
 * don't introduce a tab / dialog dep before Phase 3.
 *
 * Wire-level shape: `PortalVehicleDetailSchema` in
 * `@customer-portal/shared`. Photos are an optional embedded list. The
 * upstream BFF allowlist already strips forbidden fields (cost / margin
 * / internal memo) — this page never reads them.
 *
 * Attachments / download flow: Phase 2 surfaces a photo strip and the
 * shipping linkage (vessel / etd / eta / loading_port / destination_port).
 * Document download via signed URL lands in Phase 3 Stream E; the photo
 * `url` field is treated as opaque (PSM-signed).
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.2 (Phase 2).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PortalVehicleDetail, PortalVehiclePhoto } from '@customer-portal/shared';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '../components/ui';
import { PortalFeatureDisabledError, apiGet } from '../lib/api';
import { PortalApiError } from '../lib/apiClient';

interface DetailEnvelope {
  ok: true;
  data: { vehicle: PortalVehicleDetail };
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; vehicle: PortalVehicleDetail }
  | { kind: 'feature_disabled' }
  | { kind: 'not_found' }
  | { kind: 'error'; code: string };

export function VehicleDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [selectedPhotoId, setSelectedPhotoId] = useState<number | null>(null);

  useEffect(() => {
    if (!id) {
      setLoad({ kind: 'not_found' });
      return;
    }
    const ctrl = new AbortController();
    setLoad({ kind: 'loading' });
    setSelectedPhotoId(null);
    void (async () => {
      try {
        const res = await apiGet<DetailEnvelope>(
          `/vehicles/${encodeURIComponent(id)}`,
          ctrl.signal,
        );
        setLoad({ kind: 'success', vehicle: res.data.vehicle });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalFeatureDisabledError) {
          setLoad({ kind: 'feature_disabled' });
          return;
        }
        if (err instanceof PortalApiError) {
          if (err.status === 404) {
            setLoad({ kind: 'not_found' });
            return;
          }
          setLoad({ kind: 'error', code: err.code });
          return;
        }
        setLoad({ kind: 'error', code: 'UNKNOWN' });
      }
    })();
    return () => ctrl.abort();
  }, [id]);

  const backLink = (
    <Link
      to="/vehicles"
      style={{
        color: '#1f4ed8',
        fontSize: '0.875rem',
        textDecoration: 'none',
      }}
    >
      ← Back to vehicles
    </Link>
  );

  if (load.kind === 'idle' || load.kind === 'loading') {
    return (
      <section data-testid="vehicle-detail-page">
        <div style={{ marginBottom: '1rem' }}>{backLink}</div>
        <div data-testid="vehicle-detail-loading">
          <Spinner block label="Loading vehicle" />
        </div>
      </section>
    );
  }
  if (load.kind === 'feature_disabled') {
    return (
      <section data-testid="vehicle-detail-page">
        <div style={{ marginBottom: '1rem' }}>{backLink}</div>
        <div data-testid="vehicle-detail-feature-disabled">
          <ErrorState
            title="Vehicle details unavailable"
            description="This feature is preparing. Please check back soon."
          />
        </div>
      </section>
    );
  }
  if (load.kind === 'not_found') {
    return (
      <section data-testid="vehicle-detail-page">
        <div style={{ marginBottom: '1rem' }}>{backLink}</div>
        <div data-testid="vehicle-detail-not-found">
          <ErrorState
            title="Vehicle not found"
            description="This vehicle is no longer available, or you do not have access."
          />
        </div>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section data-testid="vehicle-detail-page">
        <div style={{ marginBottom: '1rem' }}>{backLink}</div>
        <div data-testid="vehicle-detail-error">
          <ErrorState
            title="Failed to load vehicle"
            description={`Error code: ${load.code}`}
            onRetry={() => window.location.reload()}
          />
        </div>
      </section>
    );
  }

  return (
    <VehicleDetailBody
      vehicle={load.vehicle}
      backLink={backLink}
      selectedPhotoId={selectedPhotoId}
      onSelectPhoto={setSelectedPhotoId}
    />
  );
}

interface BodyProps {
  vehicle: PortalVehicleDetail;
  backLink: JSX.Element;
  selectedPhotoId: number | null;
  onSelectPhoto: (id: number) => void;
}

function VehicleDetailBody({
  vehicle,
  backLink,
  selectedPhotoId,
  onSelectPhoto,
}: BodyProps): JSX.Element {
  const heroUrl = useMemo(() => {
    const photo = vehicle.photos.find((p) => p.id === selectedPhotoId);
    if (photo) return photo.url;
    return vehicle.photos[0]?.url ?? vehicle.thumbnail_url;
  }, [vehicle.photos, vehicle.thumbnail_url, selectedPhotoId]);

  const title = [
    vehicle.model_year != null ? String(vehicle.model_year) : null,
    vehicle.maker_name,
    vehicle.model_name,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section data-testid="vehicle-detail-page" style={{ display: 'grid', gap: '1.25rem' }}>
      <div>{backLink}</div>

      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
              {title}
            </h1>
            <VehicleStatusPill status={vehicle.status} />
          </div>
          <p
            style={{
              margin: '0.25rem 0 0',
              fontSize: '0.875rem',
              color: '#6b7280',
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            }}
            data-testid="vehicle-chassis"
          >
            {vehicle.chassis_number}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="secondary" size="sm" disabled>
            Download documents (Phase 3)
          </Button>
        </div>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '1.25rem',
          alignItems: 'start',
        }}
        className="vehicle-detail-grid"
      >
        <div style={{ display: 'grid', gap: '1rem' }}>
          <Card aria-label="Vehicle image">
            <div
              style={{
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 320,
                padding: '1rem',
              }}
              data-testid="vehicle-hero"
            >
              {heroUrl ? (
                <img
                  src={heroUrl}
                  alt={`${vehicle.maker_name} ${vehicle.model_name}`}
                  style={{
                    maxHeight: 480,
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.875rem' }}>
                  No image available
                </p>
              )}
            </div>
            {vehicle.photos.length > 0 ? (
              <CardBody>
                <PhotoStrip
                  photos={vehicle.photos}
                  selectedId={selectedPhotoId ?? vehicle.photos[0]?.id ?? null}
                  onSelect={onSelectPhoto}
                />
              </CardBody>
            ) : null}
          </Card>

          <Card aria-label="Vehicle specifications">
            <CardHeader>
              <CardTitle level={2}>Specifications</CardTitle>
            </CardHeader>
            <Table>
              <TableBody>
                {specRows(vehicle).map((r) => (
                  <TableRow key={r.label}>
                    <TableCell style={{ color: '#6b7280', width: '40%' }}>{r.label}</TableCell>
                    <TableCell style={{ fontWeight: 500 }}>{r.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card aria-label="Shipping linkage">
            <CardHeader>
              <CardTitle level={2}>Shipping</CardTitle>
            </CardHeader>
            <Table>
              <TableBody>
                {shippingRows(vehicle).map((r) => (
                  <TableRow key={r.label}>
                    <TableCell style={{ color: '#6b7280', width: '40%' }}>{r.label}</TableCell>
                    <TableCell style={{ fontWeight: 500 }}>{r.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        <aside style={{ display: 'grid', gap: '0.75rem' }}>
          <Card aria-label="Status">
            <CardHeader>
              <CardTitle level={3}>Status</CardTitle>
            </CardHeader>
            <CardBody>
              <VehicleStatusPill status={vehicle.status} />
            </CardBody>
          </Card>

          <Card aria-label="Quick info">
            <CardHeader>
              <CardTitle level={3}>Quick info</CardTitle>
            </CardHeader>
            <CardBody style={{ display: 'grid', gap: '0.5rem' }}>
              <KV label="ETA" value={formatDate(vehicle.eta)} />
              <KV label="ETD" value={formatDate(vehicle.etd)} />
              <KV label="Loading port" value={vehicle.loading_port ?? '—'} />
              <KV label="Destination port" value={vehicle.destination_port ?? '—'} />
            </CardBody>
          </Card>

          <Card aria-label="Quick actions">
            <CardHeader>
              <CardTitle level={3}>Quick actions</CardTitle>
            </CardHeader>
            <CardBody style={{ display: 'grid', gap: '0.5rem' }}>
              <Link
                to="/documents"
                style={{
                  display: 'inline-block',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#1f4ed8',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  textDecoration: 'none',
                  textAlign: 'center',
                  background: '#fff',
                }}
              >
                View documents
              </Link>
              <Link
                to="/shipments"
                style={{
                  display: 'inline-block',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#1f4ed8',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  textDecoration: 'none',
                  textAlign: 'center',
                  background: '#fff',
                }}
              >
                View shipments
              </Link>
            </CardBody>
          </Card>
        </aside>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .vehicle-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

interface PhotoStripProps {
  photos: PortalVehiclePhoto[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

function PhotoStrip({ photos, selectedId, onSelect }: PhotoStripProps): JSX.Element {
  return (
    <div data-testid="vehicle-photo-strip">
      <p
        style={{
          margin: '0 0 0.5rem',
          fontSize: '0.75rem',
          color: '#6b7280',
          fontWeight: 600,
        }}
      >
        {photos.length} photo{photos.length === 1 ? '' : 's'}
      </p>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          overflowX: 'auto',
          paddingBottom: '0.25rem',
        }}
      >
        {photos.map((p) => {
          const active = p.id === selectedId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              aria-pressed={active}
              data-testid={`vehicle-photo-thumb-${p.id}`}
              style={{
                flex: '0 0 auto',
                border: active ? '2px solid #1f4ed8' : '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                background: '#f9fafb',
                padding: 0,
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              <img
                src={p.url}
                alt={p.title || p.file_name}
                loading="lazy"
                style={{
                  display: 'block',
                  width: 96,
                  height: 64,
                  objectFit: 'cover',
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p style={{ margin: 0, fontSize: '0.6875rem', color: '#9ca3af', fontWeight: 600 }}>{label}</p>
      <p style={{ margin: 0, fontSize: '0.875rem', color: '#111827', fontWeight: 500 }}>{value}</p>
    </div>
  );
}

function VehicleStatusPill({ status }: { status: string }): JSX.Element {
  const s = status.toLowerCase();
  let bg = '#f3f4f6';
  let color = '#374151';
  if (s.includes('transit')) {
    bg = '#dbeafe';
    color = '#1e40af';
  } else if (s.includes('arrived') || s.includes('delivered')) {
    bg = '#d1fae5';
    color = '#065f46';
  } else if (s.includes('ready') || s.includes('arranged') || s.includes('loaded')) {
    bg = '#e0e7ff';
    color = '#3730a3';
  } else if (s.includes('preparing') || s.includes('inspection')) {
    bg = '#fef3c7';
    color = '#92400e';
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: bg,
        color,
      }}
    >
      {status || '—'}
    </span>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const s = String(value).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[1]}/${m[2]}/${m[3]}`;
}

function specRows(v: PortalVehicleDetail): { label: string; value: string }[] {
  return [
    { label: 'Chassis #', value: v.chassis_number },
    { label: 'Maker', value: v.maker_name },
    { label: 'Model', value: v.model_name },
    { label: 'Year', value: v.model_year != null ? String(v.model_year) : '—' },
    { label: 'Color', value: v.body_color || '—' },
    { label: 'Odometer', value: `${v.odometer_km.toLocaleString('en-US')} km` },
    { label: 'Engine', value: v.engine_cc != null ? `${v.engine_cc} cc` : '—' },
    { label: 'Transmission', value: v.transmission || '—' },
    { label: 'Drive', value: v.drive_type || '—' },
    { label: 'Fuel', value: v.fuel_type || '—' },
    { label: 'Doors', value: v.door_count != null ? String(v.door_count) : '—' },
    {
      label: 'Seats',
      value: v.seating_capacity != null ? String(v.seating_capacity) : '—',
    },
  ];
}

function shippingRows(v: PortalVehicleDetail): { label: string; value: string }[] {
  return [
    { label: 'Vessel', value: v.vessel_name || '—' },
    { label: 'Loading port', value: v.loading_port || '—' },
    { label: 'Destination port', value: v.destination_port || '—' },
    { label: 'ETD', value: formatDate(v.etd) },
    { label: 'ETA', value: formatDate(v.eta) },
  ];
}
